import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { StyleId } from '@alltrails/maps/types/Styles';

/** Persisted to localStorage, so renaming an id drops that setting for every user. */
export type MapContentId = LocalMapContentId | ToggledMapContentId;

/** Content that is not a map style. */
export type LocalMapContentId = 'cursorHints' | 'mapCoordinates' | 'routePhotos' | 'routeDirectionArrows' | 'routeSnapPreview' | 'trails' | 'pois';

type ToggledMapContentId =
  | 'distanceMarkers'
  | 'userWaypoints'
  | 'communityWaypoints'
  | 'communityActivity'
  | 'savedRoutePolyline'
  | 'waymarkedHiking'
  | 'waymarkedCycling'
  | 'waymarkedMTB';

type MapContentEntryBase = {
  label: MessageDescriptor;
};

export type LocalMapContentEntry = MapContentEntryBase & {
  defaultVisible: boolean;
  id: LocalMapContentId;
  styleId?: never;
};

type ToggledMapContentEntry = MapContentEntryBase & {
  defaultVisible?: never;
  id: ToggledMapContentId;
  styleId: StyleId;
};

export type MapContentEntry = LocalMapContentEntry | ToggledMapContentEntry;

export type MapContentGroup = {
  entries: readonly MapContentEntry[];
  id: string;
  title: MessageDescriptor;
};

const messages = defineMessages({
  general: { defaultMessage: 'General' },
  mapCoordinates: { defaultMessage: 'Map coordinates' },
  cursorHints: { defaultMessage: 'Cursor hints' },
  route: { defaultMessage: 'Route' },
  routePhotos: { defaultMessage: 'Photos' },
  routeDirectionArrows: { defaultMessage: 'Direction arrows' },
  routeSnapPreview: { defaultMessage: 'Path preview' },
  distanceMarkers: { defaultMessage: 'Distance markers' },
  savedRoutePolyline: { defaultMessage: 'Last saved route' },
  nearby: { defaultMessage: 'Nearby' },
  trails: { defaultMessage: 'Trails' },
  pois: { defaultMessage: 'Points of interest' },
  community: { defaultMessage: 'Community content' },
  userWaypoints: { defaultMessage: 'Your waypoints' },
  communityWaypoints: { defaultMessage: 'Community waypoints' },
  communityActivity: { defaultMessage: 'Community activity' },
  waymarkedTrails: { defaultMessage: 'Waymarked trails' },
  waymarkedHiking: { defaultMessage: 'Waymarked hiking' },
  waymarkedCycling: { defaultMessage: 'Waymarked cycling' },
  waymarkedMTB: { defaultMessage: 'Waymarked mountain biking' }
});

export const mapContentGroups = [
  {
    id: 'general',
    title: messages.general,
    entries: [
      { id: 'mapCoordinates', label: messages.mapCoordinates, defaultVisible: true },
      // What a click does where the cursor is, such as "Connect to next segment". Not the path preview's stats, which are data.
      { id: 'cursorHints', label: messages.cursorHints, defaultVisible: true }
    ]
  },
  {
    id: 'route',
    title: messages.route,
    entries: [
      // Off by default so a freshly opened map makes no photo tour request
      { id: 'routePhotos', label: messages.routePhotos, defaultVisible: false },
      { id: 'routeDirectionArrows', label: messages.routeDirectionArrows, defaultVisible: true },
      { id: 'routeSnapPreview', label: messages.routeSnapPreview, defaultVisible: true },
      { id: 'distanceMarkers', label: messages.distanceMarkers, styleId: 'distanceMarkers' },
      { id: 'savedRoutePolyline', label: messages.savedRoutePolyline, styleId: 'savedRoutePolyline' }
    ]
  },
  {
    id: 'nearby',
    title: messages.nearby,
    entries: [
      { id: 'trails', label: messages.trails, defaultVisible: false },
      // On by default so add to route from a POI is there without opening Map details, at the cost of an explore-service request on a fresh map
      { id: 'pois', label: messages.pois, defaultVisible: true }
    ]
  },
  {
    id: 'community',
    title: messages.community,
    entries: [
      { id: 'userWaypoints', label: messages.userWaypoints, styleId: 'userWaypoints' },
      { id: 'communityWaypoints', label: messages.communityWaypoints, styleId: 'communityWaypoints' },
      { id: 'communityActivity', label: messages.communityActivity, styleId: 'communityActivity' }
    ]
  },
  {
    id: 'waymarkedTrails',
    title: messages.waymarkedTrails,
    entries: [
      { id: 'waymarkedHiking', label: messages.waymarkedHiking, styleId: 'waymarkedHiking' },
      { id: 'waymarkedCycling', label: messages.waymarkedCycling, styleId: 'waymarkedCycling' },
      { id: 'waymarkedMTB', label: messages.waymarkedMTB, styleId: 'waymarkedMTB' }
    ]
  }
] as const satisfies readonly MapContentGroup[];

export type MapContentGroupId = (typeof mapContentGroups)[number]['id'];

export const mapContentEntries = mapContentGroups.flatMap<MapContentEntry>(group => group.entries);

export const mapContentEntriesById = Object.fromEntries(mapContentEntries.map(entry => [entry.id, entry])) as Record<MapContentId, MapContentEntry>;

export const isToggledMapContentEntry = (entry: MapContentEntry): entry is ToggledMapContentEntry => entry.styleId !== undefined;

/** Subtracted from the Layers panel, so every style has exactly one panel that applies it. */
export const mapDetailsOwnedStyleIds: StyleId[] = mapContentEntries.filter(isToggledMapContentEntry).map(entry => entry.styleId);
